import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityId,
  PayloadAction
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { DateFormat } from 'enums/dateFormats';
import { Status } from 'enums/messages';
import groupBy from 'lodash/groupBy';
import type { RootState } from 'store';
import { StaffNoteProps, StaffNotesState } from 'store/staffNotes/staffNotes.types';
import { isImageByFilename } from 'utils/files';

import { formatStaffNotes, getStaffNotesImages } from './staffNotes.settings';
import { staffNotesApiSlice } from './staffNotesApi';

const staffNotesAdapter = createEntityAdapter<StaffNoteProps, EntityId>({
  selectId: (staffNote) => staffNote._id,
  sortComparer: (a, b) => a.createdAt?.localeCompare(b.createdAt)
});

const initialState: StaffNotesState = {
  images: [],
  staffNotesList: staffNotesAdapter.getInitialState(),
  staffNotesLoadingStatus: Status.Idle,
  totalStaffNotesCount: 0
};

let lastRequestId: string;

const staffNotesSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addMatcher(staffNotesApiSlice.endpoints.initStaffNotes.matchPending, (state, action) => {
        lastRequestId = action.meta.requestId;
        state.staffNotesLoadingStatus = Status.Pending;
      })
      .addMatcher(staffNotesApiSlice.endpoints.initStaffNotes.matchFulfilled, (state, action) => {
        state.staffNotesLoadingStatus = Status.Fulfilled;
        if (lastRequestId === action.meta.requestId) {
          staffNotesAdapter.upsertMany(state.staffNotesList, action.payload.staffNotes);
          state.totalStaffNotesCount = action.payload.totalCount;
          state.images = [...getStaffNotesImages(action.payload.staffNotes), ...state.images];
        } else {
          state = initialState;
        }
      })
      .addMatcher(staffNotesApiSlice.endpoints.initStaffNotes.matchRejected, (state) => {
        state.staffNotesLoadingStatus = Status.Rejected;
      })
      .addMatcher(
        staffNotesApiSlice.endpoints.createStaffNote.matchFulfilled,
        (state, { payload }) => {
          staffNotesAdapter.upsertOne(state.staffNotesList, payload);
          if (payload.fileName && payload.filePath && isImageByFilename(payload.fileName)) {
            state.images.push(payload.filePath);
          }
        }
      )
      .addMatcher(
        staffNotesApiSlice.endpoints.createTaskStaffNote.matchFulfilled,
        (state, { payload }) => {
          if (payload.fileName && payload.filePath && isImageByFilename(payload.fileName)) {
            state.images.push(payload.filePath);
          }
        }
      );
  },
  initialState,
  name: 'staffNotes',
  reducers: {
    addStaffNote: (state, action: PayloadAction<StaffNoteProps>) => {
      staffNotesAdapter.upsertOne(state.staffNotesList, action.payload);
    },
    clearStaffNotes: () => initialState
  }
});

export const staffNotesSelectors = staffNotesAdapter.getSelectors<RootState>(
  (state) => state.staffNotes.staffNotesList
);

export const selectStaffNotes = (state: RootState) => state.staffNotes;

export const selectFormattedStaffNotes = createSelector(staffNotesSelectors.selectAll, (notes) => {
  const formattedNotes = formatStaffNotes(notes);
  return groupBy(formattedNotes, (note) => dayjs(note.date).format(DateFormat.MM_DD_YYYY));
});
export const { addStaffNote, clearStaffNotes } = staffNotesSlice.actions;

export default staffNotesSlice.reducer;
